import type { IdentityBundle } from "@beacon/client-core";
import type { ToneId } from "../i18n/tone/index.js";
import type { Bucket } from "../screens/now/display-bucket.js";
import type { RawDepth } from "../screens/now/raw-report-model.js";

export interface HomePlace {
  cell: string;
  radiusKm: number;
  placeName?: string;
}

export interface SavedPlace {
  id: string;
  cell: string;
  radiusKm: number;
  placeName?: string;
}

export type HazardKey = "fire" | "flood" | "earthquake" | "conflict" | "other";

export interface Settings {
  /** 24h local hours; non-critical alerts are muted in [startHour, endHour). */
  quietHours?: { startHour: number; endHour: number };
  /** Per-bucket in-app alert level; absent ⇒ default ("warning"). "off" = muted (map-only). */
  alertLevel?: Partial<Record<Bucket, "off" | "info" | "warning" | "critical">>;
  /** Whether the user opted into Web Push (the real subscribe is C4's seam / A6 VAPID). */
  notificationsEnabled?: boolean;
  /** How long a non-critical OS notification stays before the SW self-closes it, in ms.
   *  0 = stay until dismissed. Absent ⇒ DEFAULT_AUTO_DISMISS_MS. Critical alerts ignore this. */
  notifyAutoDismissMs?: number;
  /** Opt-in live-location following (free; foreground-only). radiusKm is the travel ring. */
  liveFollow?: { enabled: boolean; radiusKm: number };
  /** Keep the screen awake while actively following live position (default on; undefined
   *  ⇒ on). Opting out lets the device sleep — which means it cannot warn (web-app limit). */
  keepAwake?: boolean;
  /** The visitor showed install intent (saved a home zone or enabled alerts); gates the
   *  one-time install hint so it only nudges engaged users (M3 Phase 3). */
  installEngaged?: boolean;
  /** The visitor dismissed the install hint; it never returns. */
  installHintDismissed?: boolean;
  /** Selected Voice overlay. Undefined ⇒ "human" (today's copy). */
  tone?: ToneId;
  /** Per-request highest coordination-note seq the viewer has seen (unread cursor). */
  helpReadCursors?: Record<string, number>;
  /** Help events published by me and not yet echoed by the firehose (folded locally on boot
   *  so my request/offer shows immediately; cleared when the echo lands; pruned after 24 h). */
  helpPending?: Array<{ id: string; json: Record<string, unknown>; atMs: number }>;
  /** Power-user raw-report map layer (opt-in gate). Absent ⇒ disabled. `depth` is the
   *  backfill window (off/shallow/medium/deep → firehose seq budget). */
  rawReports?: { enabled: boolean; depth: RawDepth };
  /** Power-user debug overlay: show the authoritative H3 cells the alert engine keys on,
   *  beneath the vector spread cone. Absent ⇒ disabled. */
  coneDebugCells?: { enabled: boolean };
  /** Opt-in foreground alert sound (synthesized earcons). Absent ⇒ off. */
  alertSoundEnabled?: boolean;
}

export interface PersistedState {
  identityBundle?: IdentityBundle;
  home?: HomePlace;
  locale?: string;
  browseOnly?: boolean;
  /** Granted consent purposes (local mirror of the on-wire app.aid.privacy.consent log). */
  consents?: string[];
  settings?: Settings;
  /** Extra monitored places (home stays in `home`). */
  places?: SavedPlace[];
  /** Micros since epoch when this local account/identity was first created. Used by the
   *  commons mirror to scope "your cost so far" to periods on/after the join (master §6). */
  createdAt?: number;
}

export interface LocalStore {
  load(): Promise<PersistedState>;
  save(patch: Partial<PersistedState>): Promise<void>;
  clear(): Promise<void>;
}

/** Test/SSR store. */
export class MemoryStore implements LocalStore {
  #state: PersistedState = {};
  async load(): Promise<PersistedState> {
    return { ...this.#state };
  }
  async save(patch: Partial<PersistedState>): Promise<void> {
    this.#state = { ...this.#state, ...patch };
  }
  async clear(): Promise<void> {
    this.#state = {};
  }
}

const KEY = "beacon.session.v1";

/** Browser store (localStorage; sufficient for the pilot — bundle persistence is the
 *  mock-register "manual key-export" row's convenience layer). */
export class BrowserStore implements LocalStore {
  async load(): Promise<PersistedState> {
    try {
      const raw = localStorage.getItem(KEY);
      return raw ? (JSON.parse(raw) as PersistedState) : {};
    } catch {
      return {};
    }
  }
  async save(patch: Partial<PersistedState>): Promise<void> {
    const cur = await this.load();
    localStorage.setItem(KEY, JSON.stringify({ ...cur, ...patch }));
  }
  async clear(): Promise<void> {
    localStorage.removeItem(KEY);
  }
}
